import { zodResolver } from '@hookform/resolvers/zod';
import { ComponentRef, useRef } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { z } from 'zod';

import { FormGroup } from '@/components/FormGroup';
import { Button } from '@/components/ui/Button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/Dialog';
import { Textarea } from '@/components/ui/Textarea';
import i18n from '@/lib/i18n';

const feedbackFormSchema = z.object({
  message: z
    .string()
    .min(1, i18n.t('pages.VisionProduct.components.Feedback.feedbackError')),
});

type FeedbackFormData = z.infer<typeof feedbackFormSchema>;

interface FeedbackModalProps {
  isOpen: boolean;
  attributeProperty: string;
  isLoading: boolean;
  onOpenChange: () => void;
  onClose: () => void;
  onFeedback: (vote: 'up' | 'down', comments: string | undefined) => void;
}

export function FeedbackModal({
  isOpen,
  attributeProperty,
  isLoading,
  onOpenChange,
  onClose,
  onFeedback,
}: FeedbackModalProps) {
  const submitButtonRef = useRef<ComponentRef<'button'>>(null);

  const { t } = useTranslation('translation', {
    keyPrefix: 'pages.VisionProduct.components.Feedback',
  });

  const {
    formState: { errors },
    register,
    handleSubmit,
    reset,
  } = useForm<FeedbackFormData>({
    resolver: zodResolver(feedbackFormSchema),
    mode: 'onSubmit',
  });

  const onSubmit = handleSubmit((data: FeedbackFormData) => {
    onClose();
    reset();
    onFeedback('down', data.message);
  });

  return (
    <Dialog open={isOpen} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{attributeProperty}</DialogTitle>

          <DialogDescription className="sr-only" />
        </DialogHeader>

        <div className="space-y-2">
          <p className="text-sm">{t('leaveFeedback')}</p>

          <form onSubmit={onSubmit}>
            <FormGroup
              error={{
                message: errors.message?.message,
                font: 'sm',
                align: 'center',
              }}
            >
              <Textarea className="h-[120px]" {...register('message')} />
            </FormGroup>
            <button
              ref={submitButtonRef}
              type="submit"
              className="hidden"
              disabled={isLoading}
            >
              {t('submitButton')}
            </button>
          </form>
        </div>

        <DialogFooter>
          <Button variant="secondary" onClick={onClose}>
            {t('cancelButton')}
          </Button>
          <Button onClick={() => submitButtonRef.current?.click()}>
            {t('saveButton')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
